import React, { useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { RightsContext } from 'providers/RightsContext';
import { Prompt } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableRow, Switch, FormControlLabel, RadioGroup, Radio, Grid } from '@mui/material';
import { Button, TextField } from '@totalsoft/rocket-ui';
import CardNoResultsWrapper from 'components/layout/CardNoResultsWrapper';
import { GridActionsWrapper } from 'components/common/CommonStyles';
import WrappedLinearProgress from 'components/common/WrappedLinearProgress';

const DocumentScoringKnockoutCriteriaListComponent = ({
  knockoutCriteriaList,
  onPropertyChange,
  onSave,
  saving,
  knockoutCriteriaInProgress,
  evaluateKnockout,
  t,
  disabled,
  isHistory,
  isDirty,
  fromPartner
}) => {
  const { isImage, readOnlyFull } = useContext(RightsContext);

  const functionCache = useMemo(() => new Map(), []);

  const cacheFunction = useCallback(
    (index, func) => {
      let functions = functionCache.get(index);
      if (!functions) {
        functions = [];
        functionCache.set(index, functions);
      }

      let f = functions.find(x => x === func);
      if (!f) {
        f = func.bind(this);
        functions.push(f);
      }

      return f;
    },
    [functionCache]
  );

  const handleResultOkChange = useCallback(
    index =>
      cacheFunction(index, event => {
        const value = event.target.value === 'true';
        onPropertyChange(index, 'resultOk', value);
      }),
    [cacheFunction, onPropertyChange]
  );

  const handleCommentChange = useCallback(
    index =>
      cacheFunction(index, value => {
        onPropertyChange(index, 'comment', value);
      }),
    [cacheFunction, onPropertyChange]
  );

  const getEvaluationDate = useCallback(evDate => {
    if (evDate == null) return '';
    var date = new Date(evDate);
    return date.toLocaleDateString('en-GB');
  }, []);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12} sm={12}>
          <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
          <CardNoResultsWrapper content={knockoutCriteriaList}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>{t('DocumentEdit.Scoring.ScoringKnockoutCriteria.Criterion')}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t('DocumentEdit.Scoring.ScoringKnockoutCriteria.Result')}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t('DocumentEdit.Scoring.ScoringKnockoutCriteria.Comment')}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t('DocumentEdit.Scoring.ScoringKnockoutCriteria.ApprovalType')}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t('DocumentEdit.Scoring.ScoringKnockoutCriteria.EvaluationDate')}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t('DocumentEdit.Scoring.ScoringKnockoutCriteria.TriggeredBy')}</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {knockoutCriteriaList.map((x, index) => {
                  return (
                    <TableRow key={x.scoringKnockoutCriterionId}>
                      <TableCell>{x.name}</TableCell>
                      <TableCell>
                        {!knockoutCriteriaInProgress ? (
                          <RadioGroup onChange={handleResultOkChange(index)}>
                            <FormControlLabel
                              value='true'
                              control={<Radio checked={x.resultOk === true} disabled={readOnlyFull || disabled} />}
                              label={t('DocumentEdit.Scoring.ScoringKnockoutCriteria.Yes')}
                            />
                            <FormControlLabel
                              value='false'
                              control={<Radio checked={x.resultOk === false} disabled={readOnlyFull || disabled} />}
                              label={t('DocumentEdit.Scoring.ScoringKnockoutCriteria.No')}
                            />
                          </RadioGroup>
                        ) : (
                          <WrappedLinearProgress />
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          multiline
                          maxRows='3'
                          fullWidth
                          value={!x.comment && x.comment !== 0 ? '' : x.comment}
                          onChange={handleCommentChange(index)}
                          disabled={readOnlyFull || disabled}
                        />
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={<Switch checked={x.autoApproval} disabled={readOnlyFull || disabled} />}
                          label={
                            x.autoApproval
                              ? t('DocumentEdit.Scoring.ScoringKnockoutCriteria.Auto')
                              : t('DocumentEdit.Scoring.ScoringKnockoutCriteria.Manual')
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField value={getEvaluationDate(x.evaluationDate)} disabled={true} />
                      </TableCell>
                      <TableCell>
                        <TextField fullWidth value={!x.triggeredBy && x.triggeredBy !== 0 ? '' : x.triggeredBy} disabled={true} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardNoResultsWrapper>
        </Grid>
      </Grid>
      {!isHistory && !isImage && (
        <GridActionsWrapper mt={2}>
          <Button onClick={onSave} disabled={saving || disabled || !isDirty}>
            {saving ? t('Saving') + '...' : t('Save')}
          </Button>
          {fromPartner && (
            <Button onClick={evaluateKnockout} disabled={readOnlyFull || disabled || knockoutCriteriaInProgress}>
              {knockoutCriteriaInProgress
                ? t('DocumentEdit.Scoring.ScoringKnockoutCriteria.EvaluateInProgress')
                : t('DocumentEdit.Scoring.ScoringKnockoutCriteria.Evaluate')}
            </Button>
          )}
        </GridActionsWrapper>
      )}
    </>
  );
};

DocumentScoringKnockoutCriteriaListComponent.propTypes = {
  knockoutCriteriaList: PropTypes.array.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  isHistory: PropTypes.bool,
  isDirty: PropTypes.bool,
  knockoutCriteriaInProgress: PropTypes.bool,
  evaluateKnockout: PropTypes.func,
  fromPartner: PropTypes.bool
};

export default withTranslation()(DocumentScoringKnockoutCriteriaListComponent);
